import {
    Box,
    Button,
    ButtonProps,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle
} from "@mui/material";
import {
    useHistory,
    useParams
} from "react-router-dom";
import {
    useMemo,
    useState
} from "react";

import ConfirmationPrompt from "../../general/ConfirmationPrompt";
import { ExternalReportLinkRegistryFormVariables } from "@tenera/linkregistrymodels";
import { GraphQLLinkRegistryDAOFactory } from "../../../lib/linkRegistry/dao/GraphQLLinkRegistryDAOFactory";
import { LinkQRCodeScanner } from "./LinkQRCodeScanner";
import { LinkRegistryDAO } from "../../../lib/linkRegistry/dao/LinkRegistryDAO";
import { useQuery } from "react-query";

const linkRegistryDAO: LinkRegistryDAO = GraphQLLinkRegistryDAOFactory.getInstance();

type QRCodeScannerToggleProps = Omit<ButtonProps, "onClick"> & {
    showWarning?: boolean;
};

export const QRCodeScannerToggle = ({ children, showWarning = false, ...props }: QRCodeScannerToggleProps) => {
    const { id: currentLinkRegistryId } = useParams<{ id: string; }>();
    const [isButtonPressed, setIsButtonPressed] = useState(false);
    const [isWarningAccepted, setIsWarningAccepted] = useState(false);
    const [scannedLinkRegistryId, setScannedLinkRegistryId] = useState<string | undefined>(undefined);
    const history = useHistory();

    const { data: scannedLinkRegistry } = useQuery({
        queryKey: ["linkRegistry", scannedLinkRegistryId],
        queryFn: async () => {
            const linkRegistry = await linkRegistryDAO.getById(scannedLinkRegistryId!);
            // For unconfigured link, navigate to the /link-registry/:id page
            if (!linkRegistry || !linkRegistry.published) {
                navigateToLinkConfigurationPage(scannedLinkRegistryId!);
                return;
            }
            return linkRegistry;
        },
        enabled: !!scannedLinkRegistryId
    });

    const variables = useMemo<ExternalReportLinkRegistryFormVariables | undefined>(() => {
        if (!scannedLinkRegistry?.variables) {
            return undefined;
        }
        return JSON.parse(scannedLinkRegistry.variables) as ExternalReportLinkRegistryFormVariables;
    }, [scannedLinkRegistry]);

    const navigateToLinkConfigurationPage = (linkRegistryId: string) => {
        if (linkRegistryId === currentLinkRegistryId) {
            window.location.href = `/link-registry/${linkRegistryId}`;
            return;
        }
        history.push(`/link-registry/${linkRegistryId}`);
    };

    const showQRCodeScanner = isButtonPressed && (!showWarning || isWarningAccepted);

    return (
        <>
            <Button
                onClick={() => setIsButtonPressed(prev => !prev)}
                {...props}
            >
                {showQRCodeScanner ? "Stop Scanning" : children}
            </Button>
            <ConfirmationPrompt
                isVisible={isButtonPressed && !isWarningAccepted && showWarning}
                promptTitle="Are you sure?"
                promptMessage="Scanning a new QR code will bring you to a new form. You will lose any un-submitted changes you have made to this form."
                onCancelButtonClicked={() => { setIsButtonPressed(false) }}
                onConfirmButtonClicked={() => { setIsWarningAccepted(true) }}
            />
            <Dialog
                open={showQRCodeScanner}
                onClose={() => {
                    setIsButtonPressed(false);
                    setIsWarningAccepted(false);
                }}
                maxWidth="md"
                PaperProps={{
                    sx: {
                        p: 0,
                        m: 0
                    }
                }}
            >
                <DialogTitle sx={{ py: 0 }}>
                    Scan QR Code
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box width={350} height={350} p={1}>
                        <LinkQRCodeScanner
                            onValidLinkScanned={(linkRegistryId) => {
                                setScannedLinkRegistryId(linkRegistryId);
                                setIsButtonPressed(false);
                                setIsWarningAccepted(false);
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
            <Dialog
                open={!!scannedLinkRegistry?.published}
                onClose={() => setScannedLinkRegistryId(undefined)}
            >
                <DialogTitle>
                    The link you scanned is already configured to {variables?.locationName ?? "a location"}
                </DialogTitle>
                <DialogActions>
                    <Button
                        onClick={() => navigateToLinkConfigurationPage(scannedLinkRegistry?.id ?? scannedLinkRegistryId!)}
                    >
                        Re-Configure Link
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};